import React, { useEffect, useState, useRef } from 'react';
import { styled } from '@glitz/react';
import SwiperList from 'Shared/SwiperList';
import { Favourite24x24, FavouriteFull24x24 } from 'Shared/Icons/Favorite';
import { Cross48Uri, Plus48Uri } from 'Shared/Icons/Plus';
import TagViewModel from 'Shared/ViewModels/TagViewModel.type';
import * as style from 'Shared/Style';
import { appendImagePreset } from 'Shared/image-sizes';
import { PictureImage, Preset } from 'Shared/Image';
import { AspectRatioInner, AspectRatioOuter, OverlayNotABackdrop } from 'Shared/SharedComponents/atoms/AspectRatio';
import { BgTag } from 'Product/ProductTags';
import { colorFromTag } from 'Shared/utils';
import Resource from '../../../VoyadoElevate/Models/Resource.type';
import Video from 'Shared/Video/Video';
import VideoSource from 'Shared/ViewModels/VideoSourceViewModel.type';
import { Up32x32, Down32x32 } from 'Shared/Icons/ArrowCircle';

const ZoomWrap = styled.div({
  position: 'fixed',
  zIndex: 999,
  backgroundColor: style.colors.monochrome.white,
  top: 0,
  left: '0',
  pointerEvents: 'all',
  overflowY: 'scroll',
  overscrollBehaviorY: 'contain',
  height: '100vh',
  width: '100vw',
})

const ZoomedImage = styled.img({
  width: '100%',
  objectFit: 'cover',
  cursor: `url("${Cross48Uri}") 24 24, pointer`,
})

const ZoomOverlay = styled(OverlayNotABackdrop, {
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: { x: '16px', y: '8px' },
  alignItems: 'flex-end',
  justifyContent: 'flex-end',
  gap: '8px',
  position: 'fixed',
  pointerEvents: 'none',
})

const ZoomImage = styled(PictureImage, {
  pointerEvents: 'all',
  width: '48px',
  height: '65px',
  filter: 'drop-shadow(0px 0px 16px rgba(0, 0, 0, 0.16))',
  borderRadius: '8px',
  objectFit: 'cover',
  objectPosition: 'center',
  cursor: 'pointer',
  transition: {
    timingFunction: 'ease-in-out',
    duration: '0.5s',
    property: 'opacity',
  },
})

const Wrap = styled.div({
  position: 'sticky',
  top: 0,

})

const WrapRation = styled(AspectRatioOuter, {
  width: 'calc(100% + 16px)',
  paddingTop: '0px',
  height: '100vh',
  //paddingTop: `${(768 / 675) * 100}%`,
})

const ResourceWrap = styled.div({
  height: '100%',
  width: '100%'
})

const VideoWrap = styled.div({
  height: '100%',
  width: '100%',
  objectFit: 'cover',
  transition: {
    timingFunction: 'ease-in-out',
    duration: '0.5s',
    property: 'transform',
  },
})

const Image = styled(PictureImage, {
  height: '100%',
  width: '100%',
  display: 'block',
  objectFit: 'cover',
  objectPosition: 'center',
  transition: {
    timingFunction: 'ease-in-out',
    duration: '0.5s',
    property: 'transform',
  },
})

const TagsAndImageOverlay = styled(OverlayNotABackdrop, {
  height: '100%',
  width: '100%',
  display: 'flex',
  padding: { xy: '16px' },
  alignItems: 'flex-end',
  gap: '8px',
  justifyContent: 'space-between',
  pointerEvents: 'none',
})

const TagsWrap = styled.div({
  display: 'flex',
  gap: '8px',
})

const FavoriteWrap = styled.div({
  width: '48px',
  height: '48px',
  backgroundColor: style.colors.rgbaFilters.white50,
  boxShadow: '0px 0px 16px rgba(0, 0, 0, 0.16)',
  borderRadius: '100px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'all',
  cursor: 'pointer',
})

const SlideItem = styled.div({
  width: '48px',
  height: '65px',
})

const ThumbnailImage = styled(PictureImage, {
  pointerEvents: 'all',
  width: '100%',
  height: '100%',
  filter: 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.2))',
  borderRadius: '8px',
  objectFit: 'cover',
  objectPosition: 'center',
  cursor: 'pointer',
  transition: {
    timingFunction: 'ease-in-out',
    duration: '0.5s',
    property: 'opacity',
  },
})

// const FadeOutOverlay = styled.div({
//   marginBottom: '30%',
//   position: 'absolute',
//   overflow: 'hidden', /* To ensure the pseudo-element doesn't extend beyond the parent */
//   borderRadius: '8px',
//   width: '48px',
//   height: '65px',
//   left: 0,
//   right: 0,
//   bottom: 0,
//   backgroundImage: 'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1) 98%)',
//   zIndex: 1,
// });

const FavoriteIcon = styled(Favourite24x24, {
  pointerEvents: 'all',
  cursor: 'pointer',
})

const FavoriteFullIcon = styled(FavouriteFull24x24, {
  color: style.colors.monochrome.black,
  pointerEvents: 'all',
  cursor: 'pointer',
})

const CarouselWrapper = styled.div({
  cursor: 'pointer',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',

});

const CarouselImagesWrap = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  height: 'calc(100% - 48px - 24px)', // height and margin of favorite icon
  alignItems: 'center',
  justifyContent: 'end',
  paddingBottom: '72px',
})

const CarouselImagesContainer = styled.div({
  overflow: 'hidden',
})

const NavNextIcon = styled(Down32x32, {
  pointerEvents: 'all',
  cursor: 'pointer',
})

const NavPrevIcon = styled(Up32x32, {
  pointerEvents: 'all',
  cursor: 'pointer',
})

export const DesktopImageGallery = (props: {
  resources: Resource[];
  tags?: TagViewModel[];
  isFavorited?: boolean;
  toggleFavorited?: (e: any) => Promise<void>;
  headerOffset: number;
}) => {
  const swiperRef = useRef<any>(null);
  const INTERVAL = 4000;
  const { resources, tags, isFavorited, toggleFavorited, headerOffset } = props;
  const [currentItem, setCurrentItem] = useState(0);
  const [showZoom, setShowZoom] = useState(false);

  const [hasBeenClicked, setHasBeenClicked] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const [intervalId, setIntervalId] = useState(0);
  const itemCount = resources?.length ?? 0;

  useEffect(() => {
    let isMounted = true;
    if (hasBeenClicked || isHovering || showZoom) {
      clearInterval(intervalId);
      setIntervalId(0);
    } else {
      if (intervalId === 0) {
        setIntervalId(
          setInterval(() => {
            if (isMounted) {
              setCurrentItem(
                currentItem => (currentItem + 1) % itemCount
              );
              swiperRef.current.slideTo(currentItem);
            }
          }, INTERVAL),
        );
      }
    }
    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [hasBeenClicked, isHovering, showZoom]);

  useEffect(() => {
    //Reset if the number of images changes without the component being unmounted
    setCurrentItem(0);
    setHasBeenClicked(false);
  }, [resources]);

  const handlePreviousClick = () => {
    swiperRef.current.slidePrev();
    const newCurrentItem = (currentItem - 1 + resources.length) % resources.length;
    setCurrentItem(newCurrentItem);
  };

  const handleNextClick = () => {
    swiperRef.current.slideNext();
    const newCurrentItem = (currentItem + 1) % resources.length;
    setCurrentItem(newCurrentItem);
  };

  return (
    <>
      {showZoom && (
        <>
          <ZoomWrap>
            <ZoomedImage
              src={appendImagePreset(resources[currentItem].imageUrl, Preset.ItemZoom)}
              onClick={() => {
                setShowZoom(false);
              }}
            />
            <ZoomOverlay>
              {resources.map((item, index) => (
                <ZoomImage
                  key={index}
                  src={item.imageUrl}
                  preset={Preset.ItemThumb}
                  onClick={() => {
                    setCurrentItem(index);
                  }}
                  css={{
                    opacity: currentItem === index ? 1 : 0.5,
                  }}
                />
              ))}
            </ZoomOverlay>
          </ZoomWrap>
        </>
      )}
      <Wrap
        css={{
          top: `${headerOffset}px`
        }}
      >
        <WrapRation
          onMouseEnter={() => {
            setIsHovering(true);
          }}
          onMouseLeave={() => {
            setIsHovering(false);
          }}
          css={{
            top: `-${(headerOffset - 4)}px`,
          }}
        >
          <AspectRatioInner>
            <ResourceWrap>
              {resources.map((item, index) => {
                if (item.type === 'VIDEO') {
                  return (
                    <VideoWrap
                      key={index}
                      css={{
                        transform: `translateY(-${currentItem * 100}%)`,
                      }}
                    >
                      <Video
                        videoSources={[
                          {
                            url: item.videoUrl,
                            type: 'MP4',
                            device: 'ALL',
                          } as VideoSource,
                        ]}
                        previewImage={{ url: item.imageUrl }}
                        isCompact={false}
                        videoStarted={true}
                        muted={true}
                        loop
                      ></Video>
                    </VideoWrap>
                  );
                }
                return (
                  <Image
                    key={index}
                    src={item.imageUrl}
                    preset={Preset.Item}
                    css={{
                      transform: `translateY(-${currentItem * 100}%)`,
                      cursor: `url("${Plus48Uri}") 24 24, pointer`,
                    }}
                    onClick={() => {
                      setShowZoom(true);
                    }}
                  />
                );
              })}
            </ResourceWrap>
            <TagsAndImageOverlay>
              <TagsWrap>
                {tags && tags.slice(0, 2).map((tag, index) => (
                  <BgTag key={index} css={{ backgroundColor: style.colors.monochrome.white, color: colorFromTag(tag.code, tag.color) }}>
                    {tag.tag}
                  </BgTag>
                ))}
              </TagsWrap>
              <CarouselWrapper
                css={{
                  height: `calc(100% - 16px - ${headerOffset}px)`,
                }}
              >
                {!!toggleFavorited && (
                  <FavoriteWrap
                    onClick={toggleFavorited}
                  >
                    {isFavorited ? (
                      <FavoriteFullIcon />
                    ) : (
                      <FavoriteIcon />
                    )}
                  </FavoriteWrap>
                )}
                <CarouselImagesWrap>
                  <NavPrevIcon onClick={handlePreviousClick} />
                  <CarouselImagesContainer>
                    <SwiperList
                      slidesPerView={'auto'}
                      spaceBetween={8}
                      isCompact={false}
                      showNav={false}
                      vertical={true}
                      sliderRef={swiperRef}
                      onIndexChange={() => setCurrentItem(swiperRef.current.activeIndex)}
                      pagination={false}
                      freeMode={false}
                      cssMode={true}
                      height={'100%'}
                    >
                      {resources.map((item, index) => (
                        <SlideItem key={index} className="swiper-slide">
                          <ThumbnailImage
                            src={item.type === 'VIDEO' ? item.videoThumbnail : item.imageUrl}
                            preset={Preset.ItemThumb}
                            onClick={() => {
                              setHasBeenClicked(true);
                              setCurrentItem(index);
                            }}
                            css={{
                              opacity: currentItem === index ? 1 : 0.5,
                            }}
                          />
                        </SlideItem>
                      ))}
                    </SwiperList>
                  </CarouselImagesContainer>
                  <NavNextIcon onClick={handleNextClick} />
                </CarouselImagesWrap>
              </CarouselWrapper>
            </TagsAndImageOverlay>
          </AspectRatioInner>
        </WrapRation>
      </Wrap>
    </>
  );
};